import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useLang } from "../i18n/LangContext";

export const useActiveSection = (ids) => {
  const [active, setActive] = useState('');
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [ids]);
  return active;
};

export const TocSidebar = () => {
  const { ui } = useLang();
  const groups = ui.tocGroups;
  const active = useActiveSection(groups.flatMap((g) => g.items.map((i) => i.id)));
  return (
    <nav data-testid="toc-sidebar" aria-label={ui.tocAria} className="space-y-7">
      {groups.map((g) => (
        <div key={g.label}>
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            {g.label}
          </p>
          <ul className="space-y-1.5 border-l border-line">
            {g.items.map((item) => (
              <li key={item.id}>
                {item.id.startsWith('/') ? (
                  <Link
                    to={item.id}
                    data-testid={`toc-link-${item.id.replace('/', '')}`}
                    className="-ml-px block border-l-2 border-transparent py-0.5 pl-4 text-sm leading-snug text-foreground/70 transition-colors duration-150 hover:text-aegean"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    href={`#${item.id}`}
                    data-testid={`toc-link-${item.id}`}
                    className={`-ml-px block border-l-2 py-0.5 pl-4 text-sm leading-snug transition-colors duration-150 ${
                      active === item.id
                        ? 'border-aegean font-semibold text-aegean'
                        : 'border-transparent text-foreground/70 hover:text-aegean'
                    }`}
                  >
                    {item.label}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
};

export const TocMobile = () => {
  const { ui } = useLang();
  const flat = ui.tocGroups.flatMap((g) => g.items);
  const active = useActiveSection(flat.map((i) => i.id));
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid="toc-mobile"
      className="sticky top-0 z-40 -mx-6 mb-8 border-b border-line bg-paper/95 backdrop-blur lg:hidden"
    >
      <button
        type="button"
        data-testid="toc-mobile-toggle"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-6 py-3 text-xs font-bold uppercase tracking-[0.18em] text-foreground/70 transition-colors duration-150 hover:text-aegean"
      >
        {ui.tocToggle}
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <nav aria-label={ui.tocAria} className="max-h-[60vh] overflow-y-auto border-t border-line px-6 py-4">
          <ul className="flex flex-col space-y-1 border-l border-line">
            {flat.map((item) => (
              <li key={item.id}>
                {item.id.startsWith('/') ? (
                  <Link
                    to={item.id}
                    data-testid={`toc-chip-${item.id.replace('/', '')}`}
                    onClick={() => setOpen(false)}
                    className="-ml-px block border-l-2 border-transparent py-1.5 pl-4 text-sm leading-snug text-foreground/70 transition-colors duration-150 hover:text-aegean"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    href={`#${item.id}`}
                    data-testid={`toc-chip-${item.id}`}
                    onClick={() => setOpen(false)}
                    className={`-ml-px block border-l-2 py-1.5 pl-4 text-sm leading-snug transition-colors duration-150 ${
                      active === item.id
                        ? 'border-aegean font-semibold text-aegean'
                        : 'border-transparent text-foreground/70 hover:text-aegean'
                    }`}
                  >
                    {item.label}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
};
